import {
  ActivitySummaryElement,
  PolicyActivity,
} from '@clearcover/core-app-types';
import React, { FC } from 'react';

import { Button, Modal } from '@clearcover/atlas';
import { CardSectionInfoPairs } from 'Shared/components';
import { PolicyActivityDetails } from '../../Details';
import styles from './styles.module.css';
import commonStyles from '../../Common/styles.module.css';

import { formatSummaryElement } from '../../Common';
import { InfoValuePairDataType } from 'Shared/components/Card/CardSectionInfoPairs/InfoPair';
import { chunk } from 'Shared/utils';
import { usePolicyContext } from 'Policies/utils';

const ActivityData: FC<{
  infoPairs: Array<ActivitySummaryElement>;
  'data-testid': string;
}> = ({ infoPairs, 'data-testid': dataTestId }) => {
  const timeZone = usePolicyContext().details.timeZone;
  const fields: [
    string,
    InfoValuePairDataType
  ][] = infoPairs.map(summaryElement => [
    summaryElement.label,
    formatSummaryElement(summaryElement, timeZone),
  ]);
  const rows = chunk(fields, 3);
  return (
    <div data-testid={dataTestId}>
      {rows.map((row, rowIndex) => (
        <CardSectionInfoPairs
          key={rowIndex}
          className={commonStyles.activityInfoGrid}
          copyable={false}
          headers={row.map(([header]) => header)}
          data={row.map(([, value]) => value)}
          dataTestIdPrefixes={row.map(
            (_, index) => `${dataTestId}-${rowIndex}-${index.toString()}`
          )}
        />
      ))}
    </div>
  );
};

const GeneralData: FC<{
  infoPairs: Array<ActivitySummaryElement>;
  'data-testid': string;
}> = ({ infoPairs, 'data-testid': dataTestId }) => {
  const timeZone = usePolicyContext().details.timeZone;
  const fields: [
    string,
    InfoValuePairDataType
  ][] = infoPairs.map(summaryElement => [
    summaryElement.label,
    formatSummaryElement(summaryElement, timeZone),
  ]);
  return (
    <div data-testid={dataTestId}>
      <CardSectionInfoPairs
        className={commonStyles.activityInfoGrid}
        copyable={false}
        headers={fields.map(([header]) => header)}
        data={fields.map(([, value]) => value)}
        dataTestIdPrefixes={fields.map(
          (_, index) => `${dataTestId}-${index.toString()}`
        )}
      />
    </div>
  );
};

const MoreDetails: FC<{
  activity: PolicyActivity;
  'data-testid': string;
}> = ({ activity, 'data-testid': dataTestId }) => (
  <Modal
    variant="full"
    data-testid={`${dataTestId}-modal`}
    toggle={open => (
      <Button
        block
        variant="secondary"
        onClick={open}
        data-testid={`${dataTestId}-more-details`}
      >
        More Details
      </Button>
    )}
  >
    {_close => <PolicyActivityDetails activity={activity} />}
  </Modal>
);

const shouldRenderMoreDetails = (activity: PolicyActivity) => {
  return activity.type === 'POLICY_UPDATED';
};

export const PolicyActivityExpansionContents: FC<{
  activity: PolicyActivity;
  'data-testid': string;
}> = ({ activity, 'data-testid': dataTestId }) => {
  return (
    <div className={styles.contents} data-testid={dataTestId}>
      <ActivityData
        infoPairs={activity.summary.typeSpecificData}
        data-testid={`${dataTestId}-activity-data`}
      />
      <GeneralData
        infoPairs={activity.summary.generalData}
        data-testid={`${dataTestId}-general-data`}
      />
      {shouldRenderMoreDetails(activity) && (
        <MoreDetails activity={activity} data-testid={dataTestId} />
      )}
    </div>
  );
};
